import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';
import '@descope/web-component';

import { LoginRequestService } from 'src/app/services/login-request.service';
import { TokenService } from 'src/app/services/token.service';
import { switchMap, retry, tap, catchError } from 'rxjs/operators';
import { throwError, timer } from 'rxjs';
import { environment } from '@environment';

/**
 * DescopeComponent
 * * Handles the login page.
 * 1. Hosts the Descope Web Component for initial user authentication.
 * 2. On success, creates a "Login Request" in the Core API.
 * 3. Polls the Core API until the login request is approved/completed.
 * 4. Stores the final Core API JWT token.
 */
@Component({
  selector: 'app-descope',
  standalone: true,
  imports: [CommonModule, NzModalModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './descope.component.html',
  styleUrls: ['./descope.component.scss'],
})
export class DescopeComponent {
  private router = inject(Router);
  private loginRequestService = inject(LoginRequestService);
  private tokenService = inject(TokenService);

  DESCOPE_PROJECT_ID = environment.DESCOPE_PROJECT_ID;
  HOME_PAGE_PATH = '/intake';

  // --- UI State ---
  /** Controls the visibility of the "Waiting for Approval" modal. */
  isLoginRequestModalVisible = signal(false);

  /**
   * Callback triggered by the Descope Web Component upon successful authentication.
   * * @param event - The event object containing detail.sessionJwt and detail.refreshJwt.
   */
  onDescopeAuthorizationSuccess(event: any) {
    const detail = event?.detail;
    const sessionToken = detail?.sessionJwt;
    const refreshToken = detail?.refreshJwt;

    if (sessionToken && refreshToken) {
      // 1. Store Descope tokens (Legacy behavior)
      localStorage.setItem('DS', sessionToken);
      localStorage.setItem('DSR', refreshToken);

      // 2. Start the Liife Core login flow
      this.handlePostLogin(sessionToken, refreshToken);
    }
  }

  onDescopeAuthorizationError(event: any) {
    console.error('Descope Auth Error:', event);
  }

  /**
   * Manages the "Handshake" with the Liife Core API.
   * 1. Creates a Login Request using Descope tokens.
   * 2. Polls the API for completion of this request.
   * 3. Stores the final JWT and navigates to home.
   * * @param sessionToken - Descope Session JWT
   * @param refreshToken - Descope Refresh JWT
   */
  private handlePostLogin(sessionToken: string, refreshToken: string) {
    this.isLoginRequestModalVisible.set(true);

    // 1. Generate Login Request
    this.loginRequestService.createLoginRequest({
      descopeSessionToken: sessionToken,
      descopeRefreshToken: refreshToken
    }).pipe(
      // 2. Once created, switch to polling the login endpoint
      switchMap((res) => {
        const requestId = res.id;
        const requestMessage = res.message || ''; // Legacy used a message from creation response

        // Return the polling observable
        return this.loginRequestService.loginByApprovedLoginRequest(requestId, requestMessage).pipe(
          // 3. Retry logic: Wait 5s, max 60 attempts (5 minutes total)
          // This assumes the API returns an error code (like 404 or 401) while pending
          retry({
            count: 60,
            delay: (error, retryCount) => timer(5000)
          })
        );
      }),
      // 4. Handle Final Success
      tap((response) => {
        // Extract Authorization header (Legacy logic)
        const coreToken = response.headers?.get('Authorization');

        if (coreToken) {
          // Store token using TokenService (which handles localStorage 'Authorization' key)
          this.tokenService.setToken(coreToken);

          // Legacy also stored user, if your API returns the user in the body:
          // const user = response.body; 
          // this.userService.setUser(user); // If you have a UserService
        } else {
          throw new Error('No Authorization token found in response');
        }
      }),
      catchError((err) => {
        console.error('Login flow failed after polling:', err);
        return throwError(() => err);
      })
    ).subscribe({
      next: () => {
        // 5. Success: Close modal and Navigate
        this.isLoginRequestModalVisible.set(false);
        this.router.navigate([this.HOME_PAGE_PATH]);
      },
      error: () => {
        // 6. Failure: Close modal, stay on login page or show error message
        this.isLoginRequestModalVisible.set(false);
        // Optional: Show error toast here
      }
    });
  }
}